import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle,
  Clock,
  Copy,
  DollarSign,
  Download,
  Hash,
  Receipt,
  Repeat,
  Search,
  XCircle,
} from "lucide-react";
import React, { useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

import useDebounce from "../../hooks/useDebounce";
import {
  useGetTransactionsApiCallQuery,
  useLazyGetTransactionsApiCallQuery,
} from "../../services/api/transactions/transactions.api";
import { TransactionsType } from "../../services/api/transactions/transactions.type";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "sonner";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "../../services/api/auth/auth.type";
import { useGetTokenPriceConversionApiCallQuery } from "../../services/api/campaigns/campaigns.api";

// Mock transaction data
export const mockTransactions: TransactionsType[] = [
  {
    _id: "1",
    createdAt: "2023-01-01",
    transactionType: "claim",
    hash: "0xabcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890",
    from: "0x9876543210fedcba9876543210fedcba98765432",
    walletAddress: "0x1234567890abcdef1234567890abcdef12345678",
    amount: 225,
    status: "completed",
    campaignId: {
      name: "Project X Launch",
    },
    claimedAmountByUser: 225,
    isClaimed: true,
    isCompleted: true,
  },
  {
    _id: "2",
    createdAt: "2023-01-01",

    transactionType: "distribution",
    hash: "0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef",
    from: "0x9876543210fedcba9876543210fedcba98765432",
    walletAddress: "0xabcdef1234567890abcdef1234567890abcdef12",
    amount: 500,
    status: "completed",
    campaignId: {
      name: "Community Growth",
    },
    claimedAmountByUser: 225,
    isClaimed: true,
    isCompleted: true,
  },
  {
    _id: "3",
    createdAt: "2023-01-01",

    transactionType: "deposit",
    hash: "0xfedcba9876543210fedcba9876543210fedcba9876543210fedcba9876543210",
    from: "0x1111222233334444555566667777888899990000",
    walletAddress: "0x9876543210fedcba9876543210fedcba98765432",
    amount: 100000,
    status: "pending",
    campaignId: {
      name: "Community Growth",
    },
    claimedAmountByUser: 225,
    isClaimed: true,
    isCompleted: true,
  },
  {
    _id: "4",
    createdAt: "2023-01-01",

    transactionType: "refund",
    hash: "0x4444555566667777888899990000111122223333444455556666777788889999",
    from: "0x9876543210fedcba9876543210fedcba98765432",
    walletAddress: "0x5555666677778888999900001111222233334444",
    amount: 150,
    status: "failed",
    campaignId: {
      name: "Beta Testing",
    },
    claimedAmountByUser: 225,
    isClaimed: true,
    isCompleted: true,
  },
  {
    _id: "5",
    createdAt: "2023-01-01",

    transactionType: "withdrawal",
    hash: "0x7777888899990000111122223333444455556666777788889999000011112222",
    from: "0x9876543210fedcba9876543210fedcba98765432",
    walletAddress: "0x1111222233334444555566667777888899990000",
    amount: 5000,
    status: "completed",
    campaignId: {
      name: "Community Growth",
    },
    claimedAmountByUser: 225,
    isClaimed: true,
    isCompleted: true,
  },
];

const TransactionHistory: React.FC = () => {
  const [transactions, setTransactions] =
    useState<TransactionsType[]>(mockTransactions);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionsType | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(searchTerm, 500);

  // api call
  const {
    data: transactionsData,
    isLoading: isTransactionsLoading,
    isError,
  } = useGetTransactionsApiCallQuery({
    search: debouncedSearch || "",
    transactionType: "CLAIMED",
    fromDate: fromDate || "",
    toDate: toDate || "",
    page: page,
    limit: 10,
  });

  const [getTransactions] = useLazyGetTransactionsApiCallQuery();

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case "claim":
        return <ArrowUpRight className="h-4 w-4 text-green-600" />;
      case "distribution":
        return <ArrowDownLeft className="h-4 w-4 text-blue-600" />;
      case "deposit":
        return <ArrowDownLeft className="h-4 w-4 text-purple-600" />;
      case "withdrawal":
        return <ArrowUpRight className="h-4 w-4 text-orange-600" />;
      case "refund":
        return <Repeat className="h-4 w-4 text-yellow-600" />;
      case "transfer":
        return <Repeat className="h-4 w-4 text-gray-600" />;
      default:
        return <DollarSign className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: {
        className: "bg-green-100 text-green-800",
        icon: CheckCircle,
      },
      pending: { className: "bg-yellow-100 text-yellow-800", icon: Clock },
      failed: { className: "bg-red-100 text-red-800", icon: XCircle },
      cancelled: {
        className: "bg-gray-100 text-gray-800",
        icon: AlertTriangle,
      },
    } as const;

    const variant = variants[status as keyof typeof variants];
    const Icon = variant?.icon;

    return (
      <Badge className={variant?.className}>
        {/* <Icon className="h-3 w-3 mr-1" /> */}
        {Icon && <Icon className="h-3 w-3 mr-1" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getTypeLabel = (type: string) => {
    const labels = {
      claim: "User Claim",
      distribution: "Token Distribution",
      deposit: "Wallet Deposit",
      withdrawal: "Wallet Withdrawal",
      refund: "Refund",
      transfer: "Transfer",
    };
    return labels[type as keyof typeof labels];
  };

  const filteredTransactions = transactions.filter((tx) => {
    // const matchesSearch =
    //   tx.txHash.toLowerCase().includes(searchTerm.toLowerCase()) ||
    //   tx.userWalletAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
    //   tx.campaignName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    //   tx.tokenSymbol.toLowerCase().includes(searchTerm.toLowerCase());

    // const matchesType = typeFilter === "all" || tx.type === typeFilter;
    // const matchesStatus = statusFilter === "all" || tx.status === statusFilter;

    // return matchesSearch && matchesType && matchesStatus;
    return true;
  });

  const formatWalletAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTxHash = (hash: string) => {
    return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
  };

  const exportCSV = async () => {
    try {
      setIsExporting(true);
      console.log(transactionsData);

      const response = await getTransactions({
        limit: transactionsData?.result?.total,
        transactionType: "CLAIMED",
      }).unwrap();

      // const data: Transaction[] = response?.result?.data || [];

      if (response.result?.docs.length === 0) {
        toast.info("No data available to export");
        return;
      }

      const exportData = response?.result?.docs?.map((tx) => ({
        ID: tx._id,
        Status: tx.status,
        "Is Completed": tx.isCompleted ? "Yes" : "No",
        "Is Claimed": tx.isClaimed ? "Yes" : "No",
        "Campaign Name": tx.campaignId?.name || "",
        // "Campaign Status": tx.campaignId?.campaignStatus || "",
        // "Campaign Token": tx.campaignId?.tokenName || "",
        "Transaction Type": tx.transactionType,
        Token: tx.token,
        Amount: tx.amount,
        Wallet: tx.walletAddress,
        "Created At": new Date(tx.createdAt).toLocaleString(),
      }));

      // Convert JSON → worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData!);

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "User");

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `transactions__${Date.now()}.xlsx`);
      toast.success("Excel file exported successfully");
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = (fetchError?.data as ApiError) || {};
      toast.error(errorData?.responseMessage || "Error exporting file");
    } finally {
      setIsExporting(false);
    }
  };

  // const getTransactionStats = () => {
  //   const completed = transactions.filter(
  //     (tx) => tx.status === "completed"
  //   ).length;
  //   const pending = transactions.filter((tx) => tx.status === "pending").length;
  //   const failed = transactions.filter((tx) => tx.status === "failed").length;
  //   const totalVolume = transactions
  //     .filter((tx) => tx.status === "completed")
  //     .reduce((sum, tx) => sum + tx.tokenAmount, 0);

  //   return { completed, pending, failed, totalVolume };
  // };

  // const stats = getTransactionStats();
  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Transaction History
          </h1>
          <p className="text-gray-600 mt-1">
            Track all blockchain transactions and token movements
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            className="cursor-pointer"
            disabled={isExporting}
            onClick={exportCSV}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-semibold text-green-600">
                  {stats.completed}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-semibold text-yellow-600">
                  {stats.pending}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-semibold text-red-600">
                  {stats.failed}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Volume</p>
                <p className="text-2xl font-semibold">
                  {stats.totalVolume.toLocaleString()}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by hash, address, or campaign..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* From Date */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">
                  From:
                </label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-48"
                />
              </div>

              {/* To Date */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">
                  To:
                </label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-48"
                  min={fromDate || undefined} // restrict to >= fromDate
                  disabled={!fromDate} // disable until fromDate is set
                />
              </div>

              {/* <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="claim">User Claims</SelectItem>
                  <SelectItem value="distribution">Distributions</SelectItem>
                  <SelectItem value="deposit">Deposits</SelectItem>
                  <SelectItem value="withdrawal">Withdrawals</SelectItem>
                  <SelectItem value="refund">Refunds</SelectItem>
                  <SelectItem value="transfer">Transfers</SelectItem>
                </SelectContent>
              </Select> */}

              {/* <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select> */}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Hash</TableHead>
                  <TableHead>From/To</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Amount USDT</TableHead>

                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  {/* <TableHead className="text-right">Actions</TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isTransactionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : (
                  !isError &&
                  transactionsData?.result?.docs &&
                  transactionsData?.result?.docs.length > 0 &&
                  transactionsData?.result?.docs.map((transaction) => (
                    <TableRow key={transaction.hash}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {transaction?.transactionType}
                          <span className="font-medium">
                            {/* {getTypeLabel(transaction.type)} */}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Hash className="h-3 w-3 text-gray-400" />
                          <span className="font-mono text-sm">
                            {/* {formatTxHash(transaction.hash)} */}
                            {transaction?.hash || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-mono">
                            {/* From: {formatWalletAddress(transaction.from)} */}
                            From : {transaction?.from || "-"}
                          </div>
                          <div className="font-mono text-gray-500 flex">
                            {/* To: {formatWalletAddress(transaction.walletAddress)} */}
                            To : {transaction?.walletAddress}{" "}
                            {transaction?.walletAddress ? (
                              <Copy
                                className=" h-4 cursor-pointer text-gray-500 hover:text-black ml-2"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    transaction.walletAddress
                                  );
                                  toast.success("Wallet address copied!");
                                }}
                              />
                            ) : (
                              <span>--</span>
                            )}{" "}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {/* <div className="font-medium">
                        {transaction.tokenAmount.toLocaleString()}{" "}
                        {transaction.tokenSymbol}
                      </div>
                      {transaction.gasFee && transaction.gasFee > 0 && (
                        <div className="text-xs text-gray-500">
                          Gas: {transaction.gasFee} ETH
                        </div>
                      )} */}
                        {transaction?.amount}
                      </TableCell>
                      <TableCell>
                        $
                        {transaction.amount
                          ? getUSDTBalance(transaction.amount).toFixed(5)
                          : 0}{" "}
                      </TableCell>

                      <TableCell>
                        {transaction?.campaignId?.name}
                        {/* {transaction.campaignName ? (
                        <div className="text-sm">
                          <div className="font-medium">
                            {transaction.campaignName}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )} */}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(transaction.status)}
                      </TableCell>
                      <TableCell>
                        {new Date(
                          transaction.createdAt as string
                        ).toLocaleDateString()}
                        {/* <div className="text-sm">
                        {new Date(transaction.timestamp).toLocaleDateString()}
                        <div className="text-xs text-gray-500">
                          {new Date(transaction.timestamp).toLocaleTimeString()}
                        </div>
                      </div> */}
                      </TableCell>
                      {/* <TableCell className="text-right">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedTransaction(transaction)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Transaction Details</DialogTitle>
                            <DialogDescription>
                              Complete information for transaction{" "}
                              {formatTxHash(transaction.txHash)}
                            </DialogDescription>
                          </DialogHeader>

                          {selectedTransaction && (
                            <Tabs defaultValue="details" className="w-full">
                              <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="details">
                                  Transaction Details
                                </TabsTrigger>
                                <TabsTrigger value="blockchain">
                                  Blockchain Info
                                </TabsTrigger>
                              </TabsList>

                              <TabsContent
                                value="details"
                                className="space-y-4"
                              >
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Type
                                    </label>
                                    <p className="mt-1 flex items-center space-x-2">
                                      {getTransactionIcon(
                                        selectedTransaction.type
                                      )}
                                      <span>
                                        {getTypeLabel(selectedTransaction.type)}
                                      </span>
                                    </p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Status
                                    </label>
                                    <div className="mt-1">
                                      {getStatusBadge(
                                        selectedTransaction.status
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Amount
                                    </label>
                                    <p className="mt-1 font-medium">
                                      {selectedTransaction.tokenAmount.toLocaleString()}{" "}
                                      {selectedTransaction.tokenSymbol}
                                    </p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Date
                                    </label>
                                    <p className="mt-1">
                                      {new Date(
                                        selectedTransaction.timestamp
                                      ).toLocaleString()}
                                    </p>
                                  </div>
                                </div>

                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    From Address
                                  </label>
                                  <p className="mt-1 font-mono text-sm break-all">
                                    {selectedTransaction.fromAddress}
                                  </p>
                                </div>

                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    To Address
                                  </label>
                                  <p className="mt-1 font-mono text-sm break-all">
                                    {selectedTransaction.toAddress}
                                  </p>
                                </div>

                                {selectedTransaction.campaignName && (
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Campaign
                                    </label>
                                    <p className="mt-1">
                                      {selectedTransaction.campaignName}
                                    </p>
                                  </div>
                                )}

                                {selectedTransaction.notes && (
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Notes
                                    </label>
                                    <p className="mt-1 text-sm text-gray-700">
                                      {selectedTransaction.notes}
                                    </p>
                                  </div>
                                )}
                              </TabsContent>

                              <TabsContent
                                value="blockchain"
                                className="space-y-4"
                              >
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Transaction Hash
                                    </label>
                                    <p className="mt-1 font-mono text-sm break-all">
                                      {selectedTransaction.txHash}
                                    </p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Block Number
                                    </label>
                                    <p className="mt-1">
                                      {selectedTransaction.blockNumber ||
                                        "Pending"}
                                    </p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Gas Used
                                    </label>
                                    <p className="mt-1">
                                      {selectedTransaction.gasUsed?.toLocaleString() ||
                                        "N/A"}
                                    </p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Gas Fee
                                    </label>
                                    <p className="mt-1">
                                      {selectedTransaction.gasFee
                                        ? `${selectedTransaction.gasFee} ETH`
                                        : "N/A"}
                                    </p>
                                  </div>
                                </div>

                                <div className="pt-4">
                                  <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() =>
                                      window.open(
                                        `https://etherscan.io/tx/${selectedTransaction.txHash}`,
                                        "_blank"
                                      )
                                    }
                                  >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    View on Etherscan
                                  </Button>
                                </div>
                              </TabsContent>
                            </Tabs>
                          )}
                        </DialogContent>
                      </Dialog>
                    </TableCell> */}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {transactionsData?.result &&
              transactionsData?.result?.pages > 1 && (
                <div className="flex justify-end items-center mt-4 gap-2">
                  <Button
                    variant="outline"
                    disabled={page === 1}
                    onClick={() => setPage((prev) => prev - 1)}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {page} of {transactionsData.result?.pages}
                  </span>
                  <Button
                    variant="outline"
                    disabled={page === transactionsData.result.pages}
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
          </div>

          {isError && (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No transactions found
              </h3>
              <p className="text-gray-500">
                {searchTerm || typeFilter !== "all" || statusFilter !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "No transactions have been recorded yet"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransactionHistory;
